import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../authContext";
import ConfirmDeleteModal2 from "../../../components/DeletAssitant";
import LoadingSpinner from "../../../components/LoadingSpinner";
import "./groups.css";

/**
 * Tela de Gestão de Grupos — reutilizada tanto dentro do painel de moderador
 * (Management/index.js, tipo="grupos") quanto na página standalone acessada
 * pelo menu do avatar (pages/GroupManagement).
 *
 * Spec: specs/group_administration/spec.md
 * Regra permanente desta feature: nenhum arquivo é criado ou executado sem
 * autorização explícita do usuário a cada vez (ver specs/group_administration/plan.md).
 */

const GroupTypeDropdown = ({
  placeholder,
  options,
  selected,
  setSelected,
  testId,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="group-select-container">
      <div
        className={`group-select-input ${open ? "open" : ""} ${selected ? "selected" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        data-testid={testId}
      >
        {selected ? (
          selected
        ) : (
          <span className="group-select-placeholder">{placeholder}</span>
        )}
      </div>
      {open && (
        <ul className="group-select-dropdown">
          {options.map((option) => (
            <li
              key={option}
              className="group-select-option"
              onClick={() => {
                setSelected(option);
                setOpen(false);
              }}
            >
              {option}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const emptyForm = {
  name: "",
  domain: "",
  user_emails: [],
  assistant_ids: [],
  admin_assistant_ids: [],
};

function GroupsContent() {
  const { accessToken, level } = useContext(AuthContext);
  const canCreateOrEditDomainGroup = level === "dev" || level === "mod";
  const { t: tManagement } = useTranslation("management");
  const { t: tCommon } = useTranslation("common");

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableAssistants, setAvailableAssistants] = useState([]);
  const [allAssistants, setAllAssistants] = useState([]);
  const [domains, setDomains] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");

  const [choiceModalOpen, setChoiceModalOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [groupType, setGroupType] = useState(null); // "domain" | "users"
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userSelectOpen, setUserSelectOpen] = useState(false);
  const userSelectRef = useRef(null);
  const [userEmailToAdd, setUserEmailToAdd] = useState("");
  const [assistantSearchQuery, setAssistantSearchQuery] = useState("");
  const [assistantSelectOpen, setAssistantSelectOpen] = useState(false);
  const assistantSelectRef = useRef(null);
  const [formError, setFormError] = useState("");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [groupIdsPendingDelete, setGroupIdsPendingDelete] = useState([]);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    }),
    [accessToken],
  );

  const fetchGroups = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/get_all_groups`,
      {
        headers: authHeaders,
      },
    );
    const data = await res.json();
    setGroups(data.groups || []);
  };

  const fetchAvailableAssistants = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/get_available_tools`,
      {
        headers: authHeaders,
      },
    );
    const data = await res.json();
    setAvailableAssistants(data.tools || []);
  };

  const fetchAllAssistants = async () => {
    // Ponto 7 (2026-08-04): nomes de assistente exibidos (tags do grupo,
    // tags selecionadas no popup) precisam resolver para QUALQUER assistente
    // vinculado ao grupo, não só os que o manager logado é dono/admin —
    // /get_available_tools é restrito a isso (FR-006b), então o mapa de
    // nomes usa /get_all_assistants (todo usuário autenticado pode ler).
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/get_all_assistants`,
      {
        headers: authHeaders,
      },
    );
    const data = await res.json();
    setAllAssistants(data.assistants || []);
  };

  const fetchDomains = async () => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/get_domains`);
    const data = await res.json();
    setDomains((data || []).map((d) => d.dominio).filter(Boolean));
  };

  const fetchAllUsers = async () => {
    // /get_list_users (sem filtro de role) — /get_list_non_admin excluiria
    // adm/mod/dev, mas um grupo por Usuários pode incluir qualquer usuário.
    const res = await fetch(`${process.env.REACT_APP_API_URL}/get_list_users`, {
      headers: authHeaders,
    });
    const data = await res.json();
    setAllUsers(data || []);
  };

  useEffect(() => {
    // Ponto 8 (2026-08-04): o spinner da tela só some quando TODO o
    // necessário pro popup (assistentes, domínios, usuários) já carregou —
    // antes, só fetchGroups controlava o spinner, então dava pra abrir o
    // popup e cair no seletor de assistentes ainda vazio por alguns segundos.
    Promise.all([
      fetchGroups(),
      fetchAvailableAssistants(),
      fetchAllAssistants(),
      fetchDomains(),
      fetchAllUsers(),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userSelectRef.current && !userSelectRef.current.contains(e.target)) {
        setUserSelectOpen(false);
      }
      if (
        assistantSelectRef.current &&
        !assistantSelectRef.current.contains(e.target)
      ) {
        setAssistantSelectOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredGroups = useMemo(() => {
    const list = [...groups].sort((a, b) => a.name.localeCompare(b.name));
    if (!searchTerm) return list;
    return list.filter((g) =>
      g.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [groups, searchTerm]);

  const assistantNameById = useMemo(() => {
    const map = {};
    allAssistants.forEach((a) => {
      map[a.id] = a.nome || a.id;
    });
    return map;
  }, [allAssistants]);

  const openChoiceModal = () => {
    setFormError("");
    if (!canCreateOrEditDomainGroup) {
      startCreate("users");
      return;
    }
    setChoiceModalOpen(true);
  };

  const startCreate = (type) => {
    setGroupType(type);
    setEditingGroupId(null);
    setForm(emptyForm);
    setUserSearchQuery("");
    setUserSelectOpen(false);
    setUserEmailToAdd("");
    setFormError("");
    setChoiceModalOpen(false);
    setFormModalOpen(true);
  };

  const startEdit = (group) => {
    if (group.type === "domain" && !canCreateOrEditDomainGroup) return;
    setGroupType(group.type);
    setEditingGroupId(group._id);
    setForm({
      name: group.name,
      domain: group.domain || "",
      user_emails: group.user_emails || [],
      assistant_ids: group.assistant_ids || [],
      admin_assistant_ids: group.admin_assistant_ids || [],
    });
    setUserSearchQuery("");
    setUserSelectOpen(false);
    setUserEmailToAdd("");
    setAssistantSearchQuery("");
    setAssistantSelectOpen(false);
    setFormError("");
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setGroupType(null);
    setEditingGroupId(null);
    setForm(emptyForm);
    setUserSelectOpen(false);
    setAssistantSelectOpen(false);
  };

  const toggleUserEmail = (email) => {
    setForm((prev) => ({
      ...prev,
      user_emails: prev.user_emails.includes(email)
        ? prev.user_emails.filter((e) => e !== email)
        : [...prev.user_emails, email],
    }));
  };

  const addUserEmailDirectly = () => {
    const email = userEmailToAdd.trim().toLowerCase();
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!isValidEmail) {
      setFormError(tManagement("email_valido") || "Digite um e-mail válido");
      return;
    }
    // FR: e-mail ainda não cadastrado pode ser adicionado — quando essa
    // pessoa existir, já vai herdar o acesso via grupo. Sem checar
    // allowedDomains (diferente da tela do assistente): grupo por Usuários
    // aceita e-mails de qualquer domínio (US2).
    setForm((prev) =>
      prev.user_emails.includes(email)
        ? prev
        : { ...prev, user_emails: [...prev.user_emails, email] },
    );
    setUserEmailToAdd("");
  };

  const toggleAssistantId = (assistantId) => {
    setForm((prev) =>
      prev.assistant_ids.includes(assistantId)
        ? {
            // Remoção completa: sai também de admin_assistant_ids, senão o
            // grupo ficaria "administrador fantasma" de um assistente que
            // nem sequer é mais usuário dele.
            ...prev,
            assistant_ids: prev.assistant_ids.filter((id) => id !== assistantId),
            admin_assistant_ids: prev.admin_assistant_ids.filter(
              (id) => id !== assistantId,
            ),
          }
        : {
            ...prev,
            assistant_ids: [...prev.assistant_ids, assistantId],
          },
    );
  };

  const toggleAssistantAdminRole = (assistantId) => {
    setForm((prev) => ({
      ...prev,
      admin_assistant_ids: prev.admin_assistant_ids.includes(assistantId)
        ? prev.admin_assistant_ids.filter((id) => id !== assistantId)
        : [...prev.admin_assistant_ids, assistantId],
    }));
  };

  const validateForm = () => {
    if (!form.name.trim())
      return tCommon("required_field") || "Campo obrigatório";
    if (groupType === "domain" && !form.domain)
      return tCommon("required_field") || "Campo obrigatório";
    if (groupType === "users" && form.user_emails.length === 0)
      return tCommon("required_field") || "Campo obrigatório";
    return "";
  };

  const submitForm = async () => {
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    const body = {
      name: form.name.trim(),
      type: groupType,
      domain: groupType === "domain" ? form.domain : undefined,
      user_emails: groupType === "users" ? form.user_emails : undefined,
      assistant_ids: form.assistant_ids,
      admin_assistant_ids: form.admin_assistant_ids,
    };

    const isEditing = Boolean(editingGroupId);
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/${isEditing ? "put_group" : "post_group"}`,
      {
        method: isEditing ? "PUT" : "POST",
        headers: authHeaders,
        body: JSON.stringify(
          isEditing ? { group_id: editingGroupId, updated_data: body } : body,
        ),
      },
    );

    const data = await res.json();

    if (!res.ok) {
      setFormError(data.response || tManagement("duplicateGroupName"));
      return;
    }

    closeFormModal();
    fetchGroups();
  };

  const requestDeleteSingle = (groupId) => {
    setGroupIdsPendingDelete([groupId]);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    await fetch(`${process.env.REACT_APP_API_URL}/delete_groups`, {
      method: "DELETE",
      headers: authHeaders,
      body: JSON.stringify({ group_ids: groupIdsPendingDelete }),
    });

    setDeleteModalOpen(false);
    setFormModalOpen(false);
    fetchGroups();
  };

  const filteredUserOptions = allUsers.filter(
    (u) =>
      u.email.toLowerCase().includes(userSearchQuery.toLowerCase()) &&
      !form.user_emails.includes(u.email),
  );

  const filteredAssistantOptions = availableAssistants.filter(
    (a) =>
      (assistantNameById[a._id] || a._id)
        .toLowerCase()
        .includes(assistantSearchQuery.toLowerCase()) &&
      !form.assistant_ids.includes(a._id),
  );

  if (loading) {
    return (
      <div className="groups-content" style={{justifyContent: "center"}}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="groups-content">
      <div className="groups-toolbar">
        <button
          data-testid="new-group-button"
          className="new-group-button"
          onClick={openChoiceModal}
        >
          {tManagement("newGroup")}
        </button>
          <div className="groups-search-input-wrapper">
            <img
              src="/imagens/search.svg"
              alt=""
              className="groups-search-icon"
            />
            <input
              data-testid="group-search-input"
              className="groups-search-input"
              type="text"
              placeholder={tManagement("search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
      </div>

      {groups.length === 0 ? (
        <p className="groups-empty-message">{tManagement("noGroupsAdded")}</p>
      ) : (
        <ul className="groups-list">
          {filteredGroups.map((group) => (
            <li key={group._id} className="group-list-item">
              <div
                className="group-list-item-info"
                onClick={() => startEdit(group)}
                role="button"
                tabIndex={0}
              >
                <span className="group-list-item-name">{group.name}</span>
                <span className="group-list-item-tools-label">
                  {tManagement("assistantsWithAccess")}
                </span>
                <div className="group-list-item-tools">
                  {(group.assistant_ids || []).map((assistantId) => (
                    <span key={assistantId} className="group-list-item-tool-tag">
                      {assistantNameById[assistantId] ||
                        (group.assistant_names || {})[assistantId] ||
                        assistantId}
                    </span>
                  ))}
                </div>
              </div>
              <div className="group-list-item-wrapper">
                <button
                  className="group-list-item-edit"
                  onClick={() => startEdit(group)}
                  aria-label={tCommon("edit")}
                >
                  <img src="/imagens/pencil-white.svg" alt="" />
                </button>
                <button
                    data-testid="group-delete-button"
                    className="group-delete-button"
                    onClick={() => requestDeleteSingle(group._id)}
                  >
                    <img src="/imagens/delete-red.svg" alt="" />
                  </button>
                </div>
            </li>
          ))}
        </ul>
      )}

      {choiceModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => setChoiceModalOpen(false)}
        >
          <div
            className="group-type-choice-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="group-type-choice-cards">
              {canCreateOrEditDomainGroup && (
                <button
                  className="group-type-choice-domain"
                  onClick={() => startCreate("domain")}
                >
                  <span className="group-type-choice-icon">
                    <img src="/imagens/building.svg" alt="" className="icon-default" />
                    <img src="/imagens/building-hover.svg" alt="" className="icon-hover" />
                  </span>
                  <span className="group-type-choice-label">
                    {tManagement("groupTypeDomain")}
                  </span>
                  <span className="group-type-choice-subtitle">
                    {tManagement("groupTypeDomainSubtitle")}
                  </span>
                </button>
              )}
              <button
                className="group-type-choice-users"
                onClick={() => startCreate("users")}
              >
                <span className="group-type-choice-icon">
                  <img src="/imagens/users-orange.svg" alt="" className="icon-default" />
                  <img src="/imagens/users-hover.svg" alt="" className="icon-hover" />
                </span>
                <span className="group-type-choice-label">
                  {tManagement("groupTypeUsers")}
                </span>
                <span className="group-type-choice-subtitle">
                  {tManagement("groupTypeUsersSubtitle")}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {formModalOpen && (
        <div className="modal-overlay" onClick={closeFormModal}>
          <div
            className="group-form-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="group-form-header">
              <h2 className="group-form-header-title">
                {editingGroupId
                  ? tManagement("editGroup")
                  : tManagement("newGroup")}
              </h2>
              <img
                src="/imagens/x.svg"
                alt={tCommon("closeIcon")}
                className="group-form-header-icon"
                onClick={closeFormModal}
              />
            </div>

            <div className="group-form-fields">
              <span className="group-form-label">
                {tManagement("groupNamePlaceholder")}
              </span>
              <input
                data-testid="group-name-input"
                className={`group-name-input ${formError && !form.name.trim() ? "group-input-error" : ""}`}
                type="text"
                placeholder={tManagement("groupNamePlaceholder")}
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
              />

              {groupType === "domain" && (
                <div className="group-select-wrapper">
                  <span className="group-form-label">
                    {tManagement("selectDomain")}
                  </span>
                  <GroupTypeDropdown
                    testId="group-domain-select"
                    placeholder={tManagement("selectDomain")}
                    options={domains}
                    selected={form.domain}
                    setSelected={(domain) =>
                      setForm((prev) => ({ ...prev, domain }))
                    }
                  />
                </div>
              )}

              {groupType === "users" && (
                <div className="group-select-wrapper" ref={userSelectRef}>
                  <span className="group-form-label">
                    {tManagement("searchUsers")}
                  </span>
                  <div className="group-select-container">
                    {userSelectOpen ? (
                      <input
                        className="group-select-input open"
                        type="text"
                        placeholder={tManagement("searchUsers")}
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                        autoFocus
                        data-testid="group-user-select"
                      />
                    ) : (
                      <div
                        className="group-select-input"
                        onClick={() => setUserSelectOpen(true)}
                        data-testid="group-user-select"
                      >
                        <span className="group-select-placeholder">
                          {tManagement("searchUsers")}
                        </span>
                      </div>
                    )}
                    {userSelectOpen && (
                      <ul className="group-select-dropdown">
                        {filteredUserOptions.map((u) => (
                          <li
                            key={u.email}
                            className="group-select-option"
                            onClick={() => toggleUserEmail(u.email)}
                          >
                            {u.user || u.email} ({u.email})
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="group-add-email-row">
                    <input
                      className="group-name-input group-add-email-input"
                      type="text"
                      placeholder={tManagement("addByEmail")}
                      value={userEmailToAdd}
                      onChange={(e) => setUserEmailToAdd(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") addUserEmailDirectly();
                      }}
                      data-testid="group-user-add-by-email"
                    />
                    <img
                      src="/imagens/e-mail_icon.svg"
                      alt={tCommon("searchIcon")}
                      className="group-add-email-icon"
                      onClick={addUserEmailDirectly}
                      data-testid="group-user-add-by-email-button"
                    />
                  </div>
                  <div className="group-selected-users">
                    {form.user_emails.map((email) => (
                      <span key={email} className="group-selected-user-tag">
                        {email}
                        <button onClick={() => toggleUserEmail(email)}>
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {availableAssistants.length > 0 && (
                <div className="group-select-wrapper" ref={assistantSelectRef}>
                  <span className="group-form-label">
                    {tManagement("selectAssistants")}
                  </span>
                  <div className="group-select-container">
                    {assistantSelectOpen ? (
                      <input
                        className="group-select-input open"
                        type="text"
                        placeholder={tManagement("selectAssistants")}
                        value={assistantSearchQuery}
                        onChange={(e) => setAssistantSearchQuery(e.target.value)}
                        autoFocus
                        data-testid="group-assistant-select"
                      />
                    ) : (
                      <div
                        className="group-select-input"
                        onClick={() => setAssistantSelectOpen(true)}
                        data-testid="group-assistant-select"
                      >
                        <span className="group-select-placeholder">
                          {tManagement("selectAssistants")}
                        </span>
                      </div>
                    )}
                    {/* Ponto 8 (2026-08-04): sem isso, clicar abria uma
                        <ul> vazia (linha fininha) quando a busca não
                        encontrava nenhuma opção. */}
                    {assistantSelectOpen && filteredAssistantOptions.length > 0 && (
                      <ul className="group-select-dropdown">
                        {filteredAssistantOptions.map((a) => (
                          <li
                            key={a._id}
                            className="group-select-option"
                            onClick={() => toggleAssistantId(a._id)}
                          >
                            {assistantNameById[a._id] || a._id}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="group-selected-users">
                    {form.assistant_ids.map((assistantId) => {
                      const isAdmin = form.admin_assistant_ids.includes(assistantId);
                      return (
                        <span
                          key={assistantId}
                          className="group-selected-assistant-tag"
                        >
                          {assistantNameById[assistantId] || assistantId}
                          <button
                            type="button"
                            className={`group-assistant-role-toggle${isAdmin ? " admin" : ""}`}
                            onClick={() => toggleAssistantAdminRole(assistantId)}
                            title={tManagement("toggleAssistantRoleTooltip")}
                            data-testid={`group-assistant-role-${assistantId}`}
                          >
                            {isAdmin ? tManagement("roleAdmin") : tManagement("roleUserShort")}
                          </button>
                          <button
                            type="button"
                            aria-label={tCommon("delete")}
                            onClick={() => toggleAssistantId(assistantId)}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>

            {formError && <p className="group-form-error">{formError}</p>}

            <div className="group-form-actions">
              <button
                data-testid="group-add-button"
                className="group-add-button"
                onClick={submitForm}
              >
                {editingGroupId ? tCommon("save") : tManagement("addGroup")}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteModal2
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

export default GroupsContent;
